import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetAllVisitorLabReportsQuery
} from "@/api/visitorLabReportApi";
import UploadVisitorLabReport from "../components/UploadVisitorLabReport";
import { formatUsDate } from "@/utils/date";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";

const statusOptions = [
  { value: 'NOT_STARTED', label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  { value: 'WAITING_FOR_RESULT', label: 'Waiting For Result', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'READY_TO_REVIEW', label: 'Ready To Review', color: 'bg-blue-100 text-blue-800' },
  { value: 'CANCELED', label: 'Canceled', color: 'bg-red-100 text-red-800' },
];

const ALL_STATUSES_VALUE = "ALL";

export default function VisitorLabReportsList() {
  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES_VALUE);

  const { data: reports = [], isLoading: reportsLoading } = useGetAllVisitorLabReportsQuery(
    isClientRole ? { clientId: user?.clientId } : undefined
  );

  const filteredReports = statusFilter !== ALL_STATUSES_VALUE
    ? reports.filter(report => report.status === statusFilter)
    : reports;

  const getStatusBadge = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? (
      <Badge className={statusOption.color}>{statusOption.label}</Badge>
    ) : <Badge>{status}</Badge>;
  };

  if (reportsLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Visitor Lab Reports Management</h1>

        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES_VALUE}>All Statuses</SelectItem>
              {statusOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!isClientRole && (
            <Dialog>
              <DialogTrigger asChild>
                <Button>Upload Report</Button>
              </DialogTrigger>

              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Upload Visitor Lab Report</DialogTitle>
                </DialogHeader>

                <UploadVisitorLabReport />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* REPORTS TABLE */}
      <Card>
        <CardHeader>
          <CardTitle>All Visitor Lab Reports ({filteredReports.length})</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-left">
                  <th className="p-3">Client</th>
                  <th className="p-3">Test Name</th>
                  <th className="p-3">Test Order ID</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Result Document</th>
                  <th className="p-3">Uploaded At</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{report.clientName}</td>
                    <td className="p-3">{report.testName}</td>
                    <td className="p-3">{report.testOrderId}</td>
                    <td className="p-3">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="p-3">
                      {report.resultDocumentUrl ? (
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={() => window.open(report.resultDocumentUrl, '_blank')}
                        >
                          View Document
                        </Button>
                      ) : (
                        <span className="text-gray-500">No document</span>
                      )}
                    </td>
                    <td className="p-3">
                      {report.uploadedAt ? formatUsDate(report.uploadedAt) : 'N/A'}
                    </td>
                    <td className="p-3">
                      <Button size="sm" variant="outline">
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredReports.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No visitor lab reports found.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
